import React, { useState, useContext, useEffect, useCallback } from 'react';
import { AppContext } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../ui/confirm';
import { Radio, ChevronDown, ChevronUp } from 'lucide-react';
import {
  SettingsSection,
  SettingsRow,
  StatusBadge,
  InputRow,
  DangerSection,
  DangerRow
} from './SettingsUI';

const ZigbeeSettings = ({ config }) => {
  const { updateConfigSection, restartZigbee2mqtt: contextRestartZigbee2mqtt } = useContext(AppContext);
  const confirm = useConfirm();
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [coordinators, setCoordinators] = useState([]);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);
  const { apiRequest } = useAuth();
  
  const [formData, setFormData] = useState({
    websocketUrl: config.zigbee?.websocketUrl || 'ws://localhost:8080/api',
  });

  const [zigbeeConnected, setZigbeeConnected] = useState(true);
  const [restartingZigbee, setRestartingZigbee] = useState(false);
  const [recommissioning, setRecommissioning] = useState(false);
  const [currentCoordinatorPort, setCurrentCoordinatorPort] = useState(null);

  const fetchCurrentConfig = useCallback(async () => {
    try {
      const { response, data } = await apiRequest('/api/zigbee2mqtt/config');
      if (response.ok && data.config?.serial?.port) {
        setCurrentCoordinatorPort(data.config.serial.port);
        setZigbeeConnected(true);
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      setZigbeeConnected(false);
    }
  }, [apiRequest]);

  useEffect(() => {
    fetchCurrentConfig();
  }, [fetchCurrentConfig]);

  // z2m stopping closes the backend's websocket immediately, so /api/status
  // flips to disconnected before the restart POST even returns — polling for
  // "connected" reliably signals the service is back AND the backend rejoined.
  const waitForZigbeeReconnect = useCallback(async () => {
    const started = Date.now();
    await new Promise((resolve) => setTimeout(resolve, 3000));
    while (Date.now() - started < 60000) {
      try {
        const { response, data } = await apiRequest('/api/status');
        if (response.ok && data.zigbeeStatus === 'connected') return true;
      } catch (error) {
        // Hub briefly unreachable mid-restart; keep polling
      }
      await new Promise((resolve) => setTimeout(resolve, 2000));
    }
    return false;
  }, [apiRequest]);

  const discoverCoordinators = async () => {
    setDiscovering(true);
    setCoordinators([]);
    setSelectedCoordinator(null);
    try {
      const { response, data } = await apiRequest('/api/zigbee/coordinator/discover');
      if (response.ok && data.success) {
        setCoordinators(data.coordinators);
        if (data.coordinators.length === 0) {
          setStatus({ type: 'info', message: 'No coordinators found on network.' });
        }
      } else {
        throw new Error(data.message || 'Discovery failed');
      }
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setDiscovering(false);
    }
  };

  const applyCoordinator = async () => {
    if (!selectedCoordinator) return;
    
    setSaving(true);
    setStatus(null);

    try {
      const { response: configRes, data: configData } = await apiRequest('/api/zigbee2mqtt/config');
      if (!configRes.ok) throw new Error('Failed to fetch current config');

      const coordinatorUrl = `tcp://${selectedCoordinator.ip}:${selectedCoordinator.port}`;
      const newConfig = {
        ...configData.config,
        serial: { ...configData.config?.serial, port: coordinatorUrl }
      };

      const { response, data } = await apiRequest('/api/zigbee2mqtt/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: newConfig })
      });

      if (!response.ok) throw new Error(data.message || 'Failed to update');

      setCurrentCoordinatorPort(coordinatorUrl);
      setStatus({ type: 'info', message: 'Coordinator updated. Restarting Zigbee2MQTT...' });

      await contextRestartZigbee2mqtt();
      setCoordinators([]);
      setSelectedCoordinator(null);

      const reconnected = await waitForZigbeeReconnect();
      setStatus(reconnected
        ? { type: 'success', message: 'Coordinator updated. Zigbee2MQTT restarted.' }
        : { type: 'error', message: 'Zigbee2MQTT has not reconnected yet. Check again in a minute.' });
      fetchCurrentConfig();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const restartZigbee2mqtt = async () => {
    setRestartingZigbee(true);
    setStatus({ type: 'info', message: 'Zigbee2MQTT restarting...' });
    try {
      await contextRestartZigbee2mqtt();
      const reconnected = await waitForZigbeeReconnect();
      if (reconnected) {
        setStatus({ type: 'success', message: 'Zigbee2MQTT restarted.' });
        setTimeout(() => {
          setStatus((prev) => (prev?.message === 'Zigbee2MQTT restarted.' ? null : prev));
        }, 4000);
      } else {
        setStatus({ type: 'error', message: 'Zigbee2MQTT has not reconnected yet. Check again in a minute.' });
      }
      fetchCurrentConfig();
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setRestartingZigbee(false);
    }
  };

  const recommissionZigbeeNetwork = async () => {
    const ok = await confirm({
      title: 'Recommission Network',
      description: 'This will remove all paired devices. Continue?',
      confirmText: 'Continue',
      destructive: true,
    });
    if (!ok) return;

    setRecommissioning(true);
    setStatus(null);

    try {
      const { response, data } = await apiRequest('/api/zigbee2mqtt/recommission', { method: 'POST' });
      if (!response.ok) throw new Error(data.message || 'Failed');
      setStatus({ type: 'warning', message: 'Network reset. Re-pair all devices.' });
      setTimeout(() => fetchCurrentConfig(), 10000);
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setRecommissioning(false);
    }
  };

  const handleAdvancedSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    
    try {
      await updateConfigSection('zigbee', formData);
      setStatus({ type: 'success', message: 'Settings saved.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {status && (
        <Alert variant={status.type === 'error' ? 'destructive' : 'default'}>
          <AlertDescription>{status.message}</AlertDescription>
        </Alert>
      )}

      {/* Status */}
      <SettingsSection title="Status">
        <SettingsRow
          label="Connection"
          accessory={<StatusBadge status={zigbeeConnected ? 'Connected' : 'Disconnected'} />}
        />
        {currentCoordinatorPort && (
          <SettingsRow
            label="Coordinator"
            value={currentCoordinatorPort.replace('tcp://', '')}
          />
        )}
      </SettingsSection>

      {/* Coordinator Discovery */}
      <SettingsSection title="Coordinator">
        <div className="px-4 py-4 space-y-4">
          <Button
            variant="outline"
            onClick={discoverCoordinators}
            disabled={discovering}
            className="w-full h-12 rounded-2xl"
          >
            <Radio className={`w-4 h-4 mr-2 ${discovering ? 'animate-pulse' : ''}`} />
            {discovering ? 'Searching...' : 'Find Coordinators'}
          </Button>

          {coordinators.length > 0 && (
            <div className="space-y-2">
              {coordinators.map((coord, i) => {
                const isSelected = selectedCoordinator?.ip === coord.ip;
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedCoordinator(coord)}
                    className={`
                      w-full flex items-center justify-between px-4 py-3 rounded-xl text-left transition-colors
                      ${isSelected 
                        ? 'bg-primary/10 border-2 border-primary' 
                        : 'bg-muted/50 border-2 border-transparent'}
                    `}
                  >
                    <div>
                      <p className="text-sm font-medium">{coord.ip}:{coord.port}</p>
                      {coord.hostname && (
                        <p className="text-xs text-muted-foreground">{coord.hostname}</p>
                      )}
                    </div>
                    {isSelected && (
                      <span className="text-xs font-medium text-primary">Selected</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {selectedCoordinator && (
            <Button
              onClick={applyCoordinator}
              disabled={saving}
              className="w-full h-12 rounded-2xl"
            >
              {saving ? 'Applying...' : 'Apply & Restart'}
            </Button>
          )}
        </div>
      </SettingsSection>

      {/* Actions */}
      <SettingsSection title="Actions">
        <div className="px-4 py-3">
          <Button
            variant="outline"
            onClick={restartZigbee2mqtt}
            disabled={restartingZigbee}
            className="w-full h-12 rounded-2xl"
          >
            {restartingZigbee ? 'Restarting...' : 'Restart Zigbee2MQTT'}
          </Button>
        </div>
      </SettingsSection>

      {/* Advanced */}
      <SettingsSection>
        <SettingsRow
          label="Advanced"
          chevron={false}
          accessory={showAdvanced ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
          onClick={() => setShowAdvanced(!showAdvanced)}
        />
      </SettingsSection>

      {showAdvanced && (
        <form onSubmit={handleAdvancedSave}>
          <SettingsSection title="WebSocket">
            <InputRow
              label="Zigbee2MQTT URL"
              value={formData.websocketUrl}
              onChange={(e) => setFormData(prev => ({ ...prev, websocketUrl: e.target.value }))}
              required
            />
          </SettingsSection>
          <div className="px-4 pt-4">
            <Button type="submit" disabled={saving} className="w-full h-12 rounded-2xl">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      )}

      {/* Danger Zone */}
      <DangerSection>
        <DangerRow
          label="Reset Network"
          description="Remove all paired devices"
          buttonLabel={recommissioning ? 'Resetting...' : 'Reset'}
          onClick={recommissionZigbeeNetwork}
          loading={recommissioning}
        />
      </DangerSection>
    </div>
  );
};

export default ZigbeeSettings;
